import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import useApi from '../hooks/useApi';
import { useNotifications } from '../context/NotificationProvider';
import CategoryService from '../services/category.service';
import ProductService from '../services/product.service';
import AuthService from '../services/auth.service';
import useAuth from '../hooks/useAuth';
import remove from '../assets/images/remove.png';
import people from '../assets/images/people.png';
import { Button, Container, createTheme, IconButton, ThemeProvider } from '@mui/material';
import { usePrompt } from '../hooks/usePrompt';
import { useTranslation } from 'react-i18next';
import goback from '../assets/images/goback.svg';
import { useTranslatableData } from '../hooks/useTranslatable';
import { createEventDedupeKey, pushDataLayerEvent } from "../utils/gtmDataLayer";
import {
  buildEcommerceObject,
  buildItemsFromSelectedServices,
  buildUserDataObject,
  calculateValueFromItems,
} from "../utils/gtmEcommerce";
import { buildGtmEventFields } from "../utils/gtmEventFields";


const theme = createTheme({
  palette: {
    white: {
      "main": "#FFFFFF",
      "light": "#FFFFFF",
      "dark": "#FFFFFF",
      "contrastText": "#FFFFFF"
    },
    dark_yellow: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FAEF9B",
    },
    dark_white: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FFFFFF",
    },
    dark: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#393D44"
    }
  },
});

const EDIT_STEP_PATHS = [
  '/location-pro',
  '/fav-flypro',
  '/flypro-service',
  '/flypro-booking',
  '/payment'
];

const EDIT_SESSION_KEYS = [
  "editBookingMode",
  "editAddressMode",
  "modificationBookingId",
  "oldBooking",
  "newBooking",
  "productChanged",
  "therapistChanged",
  "addressChanged",
  "bookingToEdit",
  "state",
  "address"
];

function FlyProService() {
  const location = useLocation();
  const { t, i18n } = useTranslation('flyProService');
  const [domains, setDomains] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const { selectedServices: prevSelectedServices, duo: prevDuo, address: prevAddress, phone: prevPhone } = location.state || {};

  const [selectedServices, setSelectedServices] = useState(() => {
    const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}');
    const existingServices = sessionState.selectedServices;
    if (existingServices && existingServices.length > 0 && sessionStorage.getItem("editBookingMode") === "true") {
      return existingServices.map((service) => ({
        domain: null,
        category: null,
        subCategory: null,
        product: service.product || service.service, // Pre-select the product
        cat_has_products: false,
        categories: [],
        sub_categories: [],
        products: [],
        sub_cat_has_products: false,
        cat_has_subcategories: false,
        showDescriptionForId: null,
        showDescriptionForType: null,
        showProductsForId: null,
        showProductsForType: null
      }));
    }
    return prevSelectedServices || [
      { domain: null, category: null, subCategory: null, product: null, cat_has_products: false, categories: [], sub_categories: [], products: [], sub_cat_has_products: false, cat_has_subcategories: false, showDescriptionForId: null, showDescriptionForType: null, showProductsForId: null, showProductsForType: null }
    ];
  });
  const [selectedTherapist, setSelectedTherapist] = useState([]);
  const [isActive, setIsActive] = useState(prevDuo || false);
  const phone = prevPhone || JSON.parse(sessionStorage.getItem('state'))?.phone || "";
  const locationState = location.state || {};
  const savedState = JSON.parse(sessionStorage.getItem('state') || '{}');
  const updatedState = { ...savedState, ...locationState };
  const address = updatedState?.address || savedState?.address;
  const [originFlow, setOriginFlow] = useState(null);
  const [showDescriptionFor, setShowDescriptionFor] = useState({
  });
  const [showProductList, setShowProductList] = useState({

  });

  const api = useApi();
  const navigate = useNavigate();
  const notify = useNotifications();
  const { auth } = useAuth();
  const [trackingUser, setTrackingUser] = useState(auth?.user || null);

  // Use translated data for instant language switching
  const translatedDomains = useTranslatableData(domains);
  const translatedSelectedServices = useTranslatableData(selectedServices);

  const isEditBookingMode = sessionStorage.getItem("editBookingMode") === "true" || location?.state?.fromEditBooking;

  // Use `usePrompt` for navigation blocking
  usePrompt(
    isEditBookingMode,
    "Êtes-vous sûr de vouloir quitter la modification ? Vos changements ne seront pas enregistrés.",
    next => EDIT_STEP_PATHS.some(p => next.pathname.startsWith(p))
  );

  useEffect(() => {
    setTrackingUser(auth?.user || null);
  }, [auth?.user]);

  const hasPrimaryAddress = (user) =>
    Boolean(
      user?.primary_address?.id
      || user?.primary_address?.street
      || user?.primary_address?.zip_code
      || user?.primaryAddress?.id
      || user?.primaryAddress?.street
      || user?.primaryAddress?.zip_code
    );

  const resolveTrackingUser = async () => {
    if (!auth?.user?.id) {
      return trackingUser || auth?.user || null;
    }

    const currentUser = trackingUser || auth?.user;
    if (hasPrimaryAddress(currentUser)) {
      return currentUser;
    }

    try {
      const response = await AuthService.me(
        { id: auth.user.id, with: "primary_address" },
        api
      );
      const nextUser = response?.user || currentUser;
      setTrackingUser(nextUser || null);
      return nextUser || currentUser || null;
    } catch (_) {
      return currentUser || null;
    }
  };


  useEffect(() => {
    const storedState = sessionStorage.getItem('state');
    if (storedState) {
      const parsedState = JSON.parse(storedState);
      setSelectedTherapist(parsedState.selectedTherapist || []);
      setOriginFlow(parsedState.origin_flow || 'pro');
    }

    const isEditMode = sessionStorage.getItem("editBookingMode") === "true";
    const booking = JSON.parse(sessionStorage.getItem("oldBooking"));
    if (isEditMode && (booking?.therapists ?? []).length > 0) {
      setSelectedTherapist(booking.therapists);
      setOriginFlow('pro');
    }
    
    if (isEditMode && booking?.products) {
      const services = booking.products.map(product => ({
        domain: null,
        category: null,
        subCategory: null,
        product: product,
        cat_has_products: false,
        categories: [],
        sub_categories: [],
        products: [],
        sub_cat_has_products: false,
        cat_has_subcategories: false,
        showDescriptionForId: null,
        showDescriptionForType: null,
        showProductsForId: null,
        showProductsForType: null
      }));
      setSelectedServices(services);
    }
  }, []);

  const fetchDomains = async () => {
    try {
      if (!selectedTherapist.length) {
        console.warn('No therapist selected');
        return;
      }

      const domainIds = [...new Set(selectedTherapist[0].categories.map(cat => cat.domain_id).filter(id => id !== null))];

      const domainResponses = await Promise.all(
        domainIds.map(id =>
          api.get(`/domains/public/${id}`, {
            params: {
              with: 'media',
            },
            headers: { Authorization: `Bearer ${auth?.accessToken}` },
          })
        )
      );

      const filteredDomains = domainResponses
        .map(response => response.data)
        .filter(domain => domain.hotel_id == null);
      setDomains(filteredDomains);
    } catch (error) {
      console.error('Error fetching domains:', error);
      notify.error('Failed to load domains. Please try again later.');
    }
  };

  useEffect(() => {
    const autoSelectHierarchy = async () => {
      if (!isEditBookingMode || !domains.length || !selectedTherapist.length) return;

      const needsWork = selectedServices.some(s => s?.product && !s?.domain);
      if (!needsWork) return;

      const updated = [...selectedServices];
      let changed = false;

      for (let i = 0; i < updated.length; i++) {
        const svc = updated[i];
        if (svc?.domain || !svc?.product) continue;

        const product = svc.product;
        const domainId = product.domain_id;
        const productCategory = product.category;

        const domain = domains.find(d => d.id === domainId);
        if (!domain) continue;

        updated[i].domain = domain;

        const cats = await fetchCategories(domain.id);
        updated[i].categories = Array.isArray(cats) ? cats : [];

        const fetchSubcats = async (categoryId) => {
          try {
            const resp = await CategoryService.index(
              { parent_id: categoryId, is_active: 1, with: 'media' },
              api,
              auth?.accessToken ? false : true
            );
            return resp || [];
          } catch {
            return [];
          }
        };

        if (productCategory?.parent_id == null) {
          const cat = cats.find(c => c.id === productCategory.id);
          if (!cat) continue;

          updated[i].category = cat;

          const [prods, subcats] = await Promise.all([
            fetchProducts(cat.id),
            fetchSubcats(cat.id),
          ]);

          updated[i].products = prods || [];
          updated[i].sub_categories = subcats || [];
          updated[i].cat_has_products = (prods?.length || 0) > 0;
          updated[i].cat_has_subcategories = (subcats?.length || 0) > 0;

          if ((prods?.length || 0) > 0) {
            updated[i].showProductsForType = 'category';
            updated[i].showProductsForId = cat.id;
            updated[i].showProductsForIdParent = null;
          } else {
            updated[i].showProductsForType = null;
            updated[i].showProductsForId = null;
            updated[i].showProductsForIdParent = null;
          }
        } else {
          const parentCat = cats.find(c => c.id === productCategory.parent_id);
          if (!parentCat) continue;

          updated[i].category = parentCat;

          const [parentProds, subcats] = await Promise.all([
            fetchProducts(parentCat.id),
            fetchSubcats(parentCat.id),
          ]);

          updated[i].products = parentProds || [];
          updated[i].sub_categories = subcats || [];
          updated[i].cat_has_products = (parentProds?.length || 0) > 0;
          updated[i].cat_has_subcategories = (subcats?.length || 0) > 0;

          const sub = subcats.find(sc => sc.id === productCategory.id);
          if (sub) {
            updated[i].subCategory = sub;

            const [subProds, prodsCat] = await Promise.all([
              fetchProducts(sub.id),
              fetchProducts(parentCat.id),
            ]);

            updated[i].products = subProds || [];
            updated[i].productsCat = prodsCat || [];
            updated[i].sub_cat_has_products = (subProds?.length || 0) > 0;

            if ((subProds?.length || 0) > 0) {
              updated[i].showProductsForType = 'subCategory';
              updated[i].showProductsForId = sub.id;
              updated[i].showProductsForIdParent = parentCat.id;
            } else {
              updated[i].showProductsForType = null;
              updated[i].showProductsForId = null;
              updated[i].showProductsForIdParent = parentCat.id;
            }
          }
        }

        changed = true;
      }

      if (changed) setSelectedServices(updated);
    };

    autoSelectHierarchy();
  }, [domains, isEditBookingMode, selectedTherapist]);


  const fetchCategories = async (domainId) => {
    try {
      const response = await CategoryService.index(
        { domain_id: domainId, parent_id: null, is_active: 1, with: 'media,subcategories,therapist_categories' },
        api,
        auth?.accessToken ? false : true
      );

      const filteredResponse = response.filter(category => {
        const hasTherapist = category.therapist_categories.some(tc => selectedTherapist[0].id === tc.therapist_id);
        return hasTherapist;
      });

      setCategories(filteredResponse);
      return filteredResponse;
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // const fetchSubCategories = async (categoryId) => {
  //   try {
  //     const response = await api.get(`/categories/public`, {
  //       params: { parent_id: categoryId, is_active: 1, with: 'media' }
  //     });
  //     setSubCategories(response.data);
  //   } catch (error) {
  //     console.error('Error fetching subcategories:', error);
  //   }
  // };

  const fetchProducts = async (categoryId) => {
    try {
      const response = await ProductService.index(
        { category_id: categoryId, with: 'media', is_active: 1 },
        api,
        auth?.accessToken ? false : true
      );
      setProducts(response);
      return response;
    } catch (error) {
      console.error('Error fetching products:', error);
    }
    return [];
  };

  useEffect(() => {
    if (!auth || Object.keys(auth).length === 0) {
      sessionStorage.setItem('redirectAfterLogin', '/fav-flypro');
      // navigate('/login');
    }
    fetchDomains();

  }, [auth, navigate, selectedTherapist]);

  const toggleSwitch = () => {
    setIsActive(!isActive);
  };

  const handleDomainSelect = async (personIndex, domainObj) => {
    const updated = [...selectedServices];
    setShowDescriptionFor({ type: null, id: null });

    const categories = await fetchCategories(domainObj.id);

    updated[personIndex] = {
      domain: domainObj,
      category: null,
      subCategory: null,
      product: null,
      categories: categories,
      sub_categories: [],
      products: [],
      cat_has_products: false,
      sub_cat_has_products: false,
      cat_has_subcategories: false,
      showDescriptionForId: null,
      showDescriptionForType: null,
      showProductsForId: null,
      showProductsForIdParent: null,
      showProductsForType: null
    };

    setSelectedServices(updated);
    setSubCategories([]);
    setProducts([]);
  };

  const handleCategorySelect = async (personIndex, categoryObj) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].category?.id === categoryObj.id;
    if (currentlySelected) {
      updated[personIndex].category = null;
      updated[personIndex].subCategory = null;
      updated[personIndex].cat_has_products = false;
      updated[personIndex].sub_categories = [];
      updated[personIndex].sub_cat_has_products = false;
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];
      updated[personIndex].product = null;
      updated[personIndex].cat_has_subcategories = false;

      setSelectedServices(updated);
      return;
    }
    updated[personIndex].category = categoryObj;
    updated[personIndex].subCategory = null;
    updated[personIndex].product = null;
    updated[personIndex].cat_has_products = true;
    updated[personIndex].sub_categories = [];
    updated[personIndex].products = [];
    updated[personIndex].showDescriptionForId = null;
    updated[personIndex].showDescriptionForType = null;

    // Fetch Products
    const products = await fetchProducts(categoryObj.id);
    if (products.length > 0) {
      updated[personIndex].showProductsForType = "category";
      updated[personIndex].showProductsForId = categoryObj.id;
      updated[personIndex].showProductsForIdParent = null;
      updated[personIndex].products = products;
      updated[personIndex].cat_has_products = true;
    } else {
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].showProductsForIdParent = null;
      updated[personIndex].products = [];
      updated[personIndex].cat_has_products = false;
    }

    const subcats = await CategoryService.index(
      { parent_id: categoryObj.id, is_active: 1, with: 'media' },
      api,
      auth?.accessToken ? false : true
    );

    if (subcats.length > 0) {
      updated[personIndex].cat_has_subcategories = true;
      updated[personIndex].sub_categories = subcats;
    } else {
      updated[personIndex].cat_has_subcategories = false;
      updated[personIndex].sub_categories = [];
    }

    setSelectedServices(updated);
  };

  const handleSubCategorySelect = async (personIndex, subcatObj) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].subCategory?.id === subcatObj.id;
    if (currentlySelected) {
      updated[personIndex].subCategory = null;
      updated[personIndex].product = null;
      updated[personIndex].sub_cat_has_products = true;
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];

      setSelectedServices(updated);
      return;
    }
    updated[personIndex].subCategory = subcatObj;
    updated[personIndex].product = null;
    updated[personIndex].sub_cat_has_products = true;
    updated[personIndex].showDescriptionForId = null;
    updated[personIndex].showDescriptionForType = null;

    // Fetch Products under the selected subcategory
    const products = await fetchProducts(subcatObj.id);
    const products_parent = await fetchProducts(updated[personIndex].category.id);

    if (products.length > 0) {
      updated[personIndex].showProductsForType = "subCategory";
      updated[personIndex].showProductsForId = subcatObj.id;
      updated[personIndex].showProductsForIdParent = subcatObj.parent_id;
      updated[personIndex].products = products;
      updated[personIndex].productsCat = products_parent;
    } else {
      updated[personIndex].sub_cat_has_products = false;
      updated[personIndex].showProductsForType = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].showProductsForIdParent = subcatObj.parent_id;
      updated[personIndex].products = [];
      updated[personIndex].productsCat = products_parent;
    }

    setSelectedServices(updated);
  };

  const handleProductSelect = (personIndex, prodObj) => {
    const updated = [...selectedServices];
    updated[personIndex].product = prodObj;
    setSelectedServices(updated);
  };

  const addPerson = () => {
    if (selectedServices.length < 4) {
      setSelectedServices([
        ...selectedServices,
        { domain: null, category: null, subCategory: null, product: null, cat_has_products: false, categories: [], sub_categories: [], products: [], sub_cat_has_products: false, cat_has_subcategories: false, showDescriptionForId: null, showDescriptionForType: null, showProductsForId: null, showProductsForType: null }
      ]);
    }
  };

  const removePerson = (idx) => {
    setSelectedServices(selectedServices.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const anyEmpty = selectedServices.some(s => !s.product);
    if (anyEmpty) {
      notify.warn(t('please_select_a_product'));
      return;
    }

    if (!selectedTherapist.length) {
      notify.warn(t('please_select_a_flypro_before_continue'));
      return;
    }

    if (!address) {
      notify.warn(t('please_select_an_address_before_continue'));
      return;
    }

    const freshState = JSON.parse(sessionStorage.getItem('state') || '{}');
    const latestAddress = freshState?.address || address;

    // Track the service selection step as add_to_cart for FlyPro flow.
    try {
      const resolvedTrackingUser = await resolveTrackingUser();
      const items = buildItemsFromSelectedServices(selectedServices, {
        address: latestAddress,
      });
      const trackingUserData = buildUserDataObject({
        user: resolvedTrackingUser,
        address: latestAddress,
        phoneNumber: phone || resolvedTrackingUser?.phone || auth?.user?.phone,
        emailAddress: resolvedTrackingUser?.email || auth?.user?.email,
        isConnectedUser: Boolean(auth?.user?.id),
      });
      if (items.length > 0) {
        const value = calculateValueFromItems(items);
        const itemsSignature = items.map((item) => item.item_id).join(",");

        pushDataLayerEvent(
          "add_to_cart",
          {
            user_data: trackingUserData,
            ...buildGtmEventFields({
              language: i18n.language,
              user: resolvedTrackingUser || auth?.user,
            }),
            ecommerce: buildEcommerceObject({
              currency: "CHF",
              value,
              items,
            }),
          },
          {
            dedupeKey: createEventDedupeKey(
              "add_to_cart",
              "flypro_service",
              itemsSignature,
              latestAddress?.zip_code,
              selectedServices.length
            ),
            dedupeTtlMs: 30000,
          }
        );
      }
    } catch (trackingError) {
      console.error("Failed to push flypro add_to_cart event:", trackingError);
    }

    const paymentState = {
      selectedServices,
      isActive,
      selectedTherapist,
      address: address,
      phone,
      originFlow,
      // duo: selectedServices.length === 2 && isActive
    };

    const newBooking = {
      ...(JSON.parse(sessionStorage.getItem("newBooking") || "{}")),
      products: selectedServices.map(s => s.product)
    };
    sessionStorage.setItem("newBooking", JSON.stringify(newBooking));

    sessionStorage.setItem('state', JSON.stringify(paymentState));
    navigate('/flypro-booking', { state: paymentState });
  };

  const toggleDescription = (type, id, personIndex) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex].showDescriptionForId === id;
    if (currentlySelected) {
      updated[personIndex].showDescriptionForId = null;
      updated[personIndex].showDescriptionForType = null;
      setSelectedServices(updated);
      return;
    }
    // updated[personIndex].showProductsForType = null;
    // updated[personIndex].showProductsForId = null;
    updated[personIndex].showDescriptionForId = id;
    updated[personIndex].showDescriptionForType = type;
    // if (type === "category") {
    //   updated[personIndex].category = null;
    // }
    // else if (type === "subCategory") {
    //   updated[personIndex].subCategory = null;
    // }
    // updated[personIndex].product = null;
    setSelectedServices(updated);
  };
  const handleGoBackClick = (personIndex) => {
    const updated = [...selectedServices];
    const currentlySelected = updated[personIndex];

    if (currentlySelected && currentlySelected.domain && !currentlySelected.category) {
      updated[personIndex].domain = null;
      setSelectedServices(updated);
      return;
    } else if (currentlySelected && currentlySelected.category && !currentlySelected.subCategory && currentlySelected.cat_has_subcategories) {
      // updated[personIndex].domain = null;
      updated[personIndex].category = null;
      updated[personIndex].product = null;
      // updated[personIndex].categories = [];
      updated[personIndex].products = [];
      updated[personIndex].showProductsForId = null;
      setSelectedServices(updated);
      return;
    } else if (currentlySelected && currentlySelected.subCategory) {
      updated[personIndex].category = null;
      updated[personIndex].subCategory = null;
      updated[personIndex].product = null;
      updated[personIndex].showProductsForId = null;
      updated[personIndex].products = [];
      setSelectedServices(updated);
      return;
    } else if (currentlySelected && currentlySelected.category && !currentlySelected.subCategory && currentlySelected.cat_has_subcategories) {
      updated[personIndex].domain = null;
      updated[personIndex].category = null;
      updated[personIndex].product = null;
      updated[personIndex].categories = [];
      updated[personIndex].products = [];
      updated[personIndex].showProductsForId = null;
      setSelectedServices(updated);
      return;

    }

  };

  return (
    <div className="flypro-service-page booking-service-page booking-service-page--flypro">
      <Header className="header--flypro-flow" compactLanguage />
      <div className={`container container-location row-${selectedServices.length}`}>
        {translatedSelectedServices[0].domain && selectedServices.length === 1 ? (
          <Container className="booking-service-page__title-row" sx={{ display: 'flex', padding: '10px 0 !important' }}>
            <ThemeProvider theme={theme}>
              <Button variant="outlined" onClick={() => handleGoBackClick(0)} color='dark' type="button"
                sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '50px', minHeight: '40px' }}>
                <img className="https-app" src={goback} alt="Info" />
              </Button>
            </ThemeProvider>
            <h2 className='h-goback'>{t('flyProService:choose_service_type')}</h2>
          </Container>
        ) : (
          <h2>
            {selectedServices.length > 1
              ? t('choose_service_type_plus')
              : t('choose_service_type')}
          </h2>
        )}
        <form className="service-form booking-service-page__form" onSubmit={handleSubmit}>
          <div className='content-row'>
            {translatedSelectedServices.map((person, idx) => (
              <div key={idx} className="person-services">
                {selectedServices.length > 1 && (
                  <h3 className='person-title'>
                    <span>
                      {selectedServices.length > 1 && translatedSelectedServices[idx].domain && (
                        <img className="https-app" src={goback} alt="Info" onClick={() => handleGoBackClick(idx)} />
                      )}
                      <img className="people" src={people} alt="icon" />
                      {idx === 0 ? t('me') : `${t('guest')} ${idx}`}
                    </span>
                    {idx > 0 && (
                      <button
                        type='button'
                        className='person-remove'
                        onClick={() => removePerson(idx)}
                      >
                        <img className='remove' src={remove} alt="Supprimer" />
                      </button>
                    )}
                  </h3>
                )}
                {/* Select Domain */}
                {(!person.domain || (person.domain && !person.categories.length)) && (
                  <div className="services-container">
                    {translatedDomains.map(dom => {
                      const isSelected = person.domain?.id === dom.id;
                      return (
                        <div key={dom.id} className="service-wrapper">
                          <div
                            className={`service-card ${isSelected ? 'selected' : ''}`}
                            style={{
                              background: `linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.5)), url(${dom?.media[0]?.original_url}) no-repeat center center / cover`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'center center',
                              backgroundSize: 'cover',
                            }}


                          >
                            <div className="service-info">
                              <div className="service-text">
                                <h3>{dom.title}</h3>
                                {dom.description !== null && dom.description !== '' && (
                                  <ThemeProvider theme={theme}>
                                    <Button variant="text" color='white' onClick={() => toggleDescription("domain", dom.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('flyProService:more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                  </ThemeProvider>
                                )}
                              </div>
                              {isSelected ? (
                                <IconButton onClick={() => handleDomainSelect(idx, dom)}>
                                  <img src='assets/images/check.png' height={20} width={20} />
                                </IconButton>
                              ) : (
                                <ThemeProvider theme={theme}>
                                  <Button variant="outlined" color='white' onClick={() => handleDomainSelect(idx, dom)} sx={{ borderRadius: '15px' }}>{t('flyProService:select')}</Button>
                                </ThemeProvider>
                              )}
                            </div>
                          </div>
                          {person.showDescriptionForType === "domain" && person.showDescriptionForId === dom.id && <div className="service-description">{dom.description}</div>}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* show category */}
                <div className="services-container">
                  {person.categories?.length > 0 && person.categories.map(cat => {
                    const isSelected = person.category?.id === cat.id;
                    return person.domain && (!person.category || (person.category && !person.sub_categories.length) || (person.category && person.cat_has_products && (!person.cat_has_subcategories || (person.cat_has_subcategories && person.category.id === cat.id)))) && (
                      <div key={cat.id} className="service-wrapper">
                        <div
                          className={`service-card ${isSelected ? 'selected' : ''}`}
                          style={{
                            background: `linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.5)), url(${cat?.media[0]?.original_url}) no-repeat center center / cover`,
                          }}
                        >
                          <div className="service-info">
                            <div className="service-text">
                              <h3>{cat.title}</h3>
                              {cat.description !== null && cat.description !== '' && (
                                <ThemeProvider theme={theme}>
                                  <Button variant="text" color='white' onClick={() => toggleDescription("category", cat.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('flyProService:more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                </ThemeProvider>
                              )}
                            </div>
                            {isSelected ? (
                              <IconButton onClick={() => handleCategorySelect(idx, cat)}>
                                <img src='assets/images/check.png' height={20} width={20} />
                              </IconButton>
                            ) : (
                              <ThemeProvider theme={theme}>
                                <Button variant="outlined" color='white' onClick={() => handleCategorySelect(idx, cat)} sx={{ borderRadius: '15px' }}>{t('flyProService:select')}</Button>
                              </ThemeProvider>
                            )}
                          </div>
                        </div>
                        {person.showDescriptionForType === "category" && person.showDescriptionForId === cat.id && <div className="service-description">{cat.description}</div>}
                        {person.showProductsForType === "category" && person.showProductsForId === cat.id && <div className='form-duree'>
                          <div className="form-group serv-form">
                            <div
                              className={`btn-group group-3 ${person.products?.length > 2 ? 'service-product-group--compact' : ''}`}
                              role="group"
                              style={{ '--service-product-count': Math.max(person.products?.length || 1, 1) }}
                            >
                              {(() => {
                                const hiddenProducts = person.products.filter(product => product.is_hidden);
                                let popularProduct = null;

                                if (hiddenProducts.length === 1) {
                                  // Un seul produit caché => il est populaire
                                  popularProduct = hiddenProducts[0];
                                } else if (hiddenProducts.length > 1) {
                                  // Plusieurs produits cachés => on prend celui avec le plus de bookings
                                  popularProduct = hiddenProducts.reduce((max, product) =>
                                    (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                    hiddenProducts[0]
                                  );
                                } else {
                                  // Aucun produit caché => on prend le produit avec le plus de bookings parmi tous
                                  popularProduct = person.products.reduce((max, product) =>
                                    (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                    person.products[0]
                                  );
                                }

                                return person.products.map(product => (
                                  <button
                                    type="button"
                                    key={product.id}
                                    className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === popularProduct.id ? 'product-popular' : ''}`}
                                    onClick={() => handleProductSelect(idx, product)}
                                  >
                                    <span>{product.duration} min</span>
                                    <br />
                                    <span className="price">{product.price} chf</span>
                                    {product.id === popularProduct.id && (
                                      <div className="popular-product">
                                      </div>
                                    )}
                                  </button>
                                ));
                              })()}
                            </div>

                          </div>
                        </div>
                        }{isSelected && person.showProductsForIdParent === cat.id && <div className='form-duree'>
                          <div className="form-group serv-form">
                            <div
                              className={`btn-group group-3 ${person.productsCat?.length > 2 ? 'service-product-group--compact' : ''}`}
                              role="group"
                              style={{ '--service-product-count': Math.max(person.productsCat?.length || 1, 1) }}
                            >
                              {(() => {
                                const hiddenProducts = person.productsCat.filter(product => product.is_hidden);
                                let popularProduct = null;

                                if (hiddenProducts.length === 1) {
                                  // S'il y a un seul produit caché, il est populaire
                                  popularProduct = hiddenProducts[0];
                                } else if (hiddenProducts.length > 1) {
                                  // Plusieurs cachés : choisir celui avec le plus de bookings
                                  popularProduct = hiddenProducts.reduce((max, product) =>
                                    (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                    hiddenProducts[0]
                                  );
                                } else {
                                  // Aucun produit caché : choisir le plus réservé parmi tous
                                  popularProduct = person.productsCat.reduce((max, product) =>
                                    (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                    person.productsCat[0]
                                  );
                                }

                                return person.productsCat.map(product => (
                                  <button
                                    type="button"
                                    key={product.id}
                                    className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === popularProduct.id ? 'product-popular' : ''}`}
                                    onClick={() => handleProductSelect(idx, product)}
                                  >
                                    <span>{product.duration} min</span>
                                    <br />
                                    <span className='price'>{product.price} chf</span>
                                    {product.id === popularProduct.id && (
                                      <div className='popular-product'>
                                      </div>
                                    )}
                                  </button>
                                ));
                              })()}
                            </div>

                          </div>
                        </div>
                        }
                      </div>
                    );
                  })}
                </div>

                {/* show subCategories */}
                {person.domain && person.category && person.sub_categories.length > 0 && (!person.subCategory || (person.subCategory && !person.products.length) || (person.subCategory && person.sub_cat_has_products)) && (
                  <div className="services-container">
                    {person.sub_categories.map(sc => {
                      const isSelected = person.subCategory?.id === sc.id;
                      return (
                        <div key={sc.id} className="service-wrapper">
                          <div
                            className={`service-card ${isSelected ? 'selected' : ''}`}
                            style={{
                              background: `linear-gradient(135deg, rgba(0,0,0,0.8), rgba(0,0,0,0.5)), url(${sc?.media[0]?.original_url}) center/cover no-repeat`
                            }}
                          >
                            <div className="service-info">
                              <div className="service-text">
                                <h3>{sc.title}</h3>
                                {sc.description !== null && sc.description !== '' && (
                                  <ThemeProvider theme={theme}>
                                    <Button variant="text" color='white' onClick={() => toggleDescription("subCategory", sc.id, idx)} sx={{ opacity: 0.7, gap: '5px', padding: 0 }}>{t('flyProService:more_info')} <img src="assets/images/icon-expand.svg" /></Button>
                                  </ThemeProvider>
                                )}
                              </div>
                              {isSelected ? (
                                <IconButton onClick={() => {handleSubCategorySelect(idx, sc); }}>
                                  <img src='assets/images/check.png' height={20} width={20} />
                                </IconButton>
                              ) : (
                                <ThemeProvider theme={theme}>
                                  <Button variant="outlined" color='white' onClick={() => handleSubCategorySelect(idx, sc)} sx={{ borderRadius: '15px' }}>{t('flyProService:select')}</Button>
                                </ThemeProvider>
                              )}
                            </div>
                          </div>
                          {person.showDescriptionForType === "subCategory" && person.showDescriptionForId === sc.id && <div className="service-description">{sc.description}</div>}
                          {person.showProductsForType === "subCategory" && person.showProductsForId === sc.id && <div className='form-duree'>
                            <div className="form-group serv-form">
                              <div
                                className={`btn-group group-3 ${person.products?.length > 2 ? 'service-product-group--compact' : ''}`}
                                role="group"
                                style={{ '--service-product-count': Math.max(person.products?.length || 1, 1) }}
                              >
                                {(() => {
                                  const hiddenProducts = person.products.filter(product => product.is_hidden);
                                  let popularProduct = null;

                                  if (hiddenProducts.length === 1) {
                                    // Un seul produit caché → il est populaire
                                    popularProduct = hiddenProducts[0];
                                  } else if (hiddenProducts.length > 1) {
                                    // Plusieurs produits cachés → choisir celui avec le plus de bookings
                                    popularProduct = hiddenProducts.reduce((max, product) =>
                                      (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                      hiddenProducts[0]
                                    );
                                  } else {
                                    // Aucun produit caché → choisir le plus réservé de tous
                                    popularProduct = person.products.reduce((max, product) =>
                                      (product.bookings?.length || 0) > (max.bookings?.length || 0) ? product : max,
                                      person.products[0]
                                    );
                                  }

                                  return person.products.map(product => (
                                    <button
                                      type="button"
                                      key={product.id}
                                      className={`btn-default 
          ${selectedServices[idx]?.product?.id === product.id ? 'btn-checked' : 'btn-unchecked'} 
          ${product.id === popularProduct.id ? 'product-popular' : ''}`}
                                      onClick={() => handleProductSelect(idx, product)}
                                    >
                                      <span>{product.duration} min</span>
                                      <br />
                                      <span className='price'>{product.price} chf</span>
                                      {product.id === popularProduct.id && (
                                        <div className='popular-product'>
                                        </div>
                                      )}
                                    </button>
                                  ));
                                })()}
                              </div>

                            </div>
                          </div>
                          }
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Container className="booking-service-page__actions" sx={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', padding: '0 !important' }}>
            <ThemeProvider theme={theme}>
              <Button className="booking-service-page__action-button booking-service-page__add-treatment-button" variant="outlined" color='dark' type="button"
                onClick={addPerson} sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>{t('flyProService:add_person')}</Button>
              <Button className="booking-service-page__action-button" variant="contained" color='dark_yellow' type="submit" sx={{ textTransform: 'none', borderRadius: '15px', width: '100%', maxWidth: '315px', minHeight: '57px' }}>{t('flyProService:next')}</Button>
            </ThemeProvider>
          </Container>

        </form>
      </div>
    </div>
  );
}

export default FlyProService;
